import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { QuoteCTA } from "@/components/QuoteCTA";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ArrowRight, RefreshCw } from "lucide-react";


export const Route = createFileRoute("/find-your-shade")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Find Your Shade — Patio Shade Quiz (San Antonio & Austin) | Texas Made Shade",
      description:
        "60-second quiz: retractable awning, sail shade, drop screen, pergola cover or motorized patio shade? Built for San Antonio & Austin patios.",
      socialTitle: "Find Your Shade — Texas Patio Shade Quiz",
      socialDescription: "Answer 5 questions and get a custom shade recommendation for your San Antonio or Austin patio.",
      keywords:
        "patio shade quiz, find my shade, what kind of patio shade do I need, retractable awning vs sail shade, motorized patio shades Austin, motorized shades San Antonio, automatic patio shade, smart shades Austin TX, awning company San Antonio, Texas Made Shade",
    }),
    scripts: serviceHeadScripts({
      path: "/find-your-shade",
      serviceName: "Find Your Shade Quiz",
      breadcrumbName: "Find Your Shade",
      description: "Interactive quiz recommending the right shade product (retractable awning, sail shade, drop screen, pergola cover) for San Antonio and Austin patios.",
      faqs: [
        { q: "How does the quiz work?", a: "Five quick questions about your patio, sun direction, wind exposure and budget. We recommend a product category and the next step." },
        { q: "Is the recommendation a quote?", a: "No — it's a starting point. After the quiz you can submit photos for a written, itemized quote." },
      ],
    }),
  }),
  component: QuizPage,
});

type Answer = { label: string; weights: Partial<Record<Product, number>> };
type Product = "solair" | "sail" | "screen" | "pergola" | "metal";
type Q = { id: string; q: string; answers: Answer[] };

const QUESTIONS: Q[] = [
  {
    id: "goal",
    q: "What's the main goal for your patio?",
    answers: [
      { label: "Shade on demand (open / close when I want)", weights: { solair: 3, screen: 1 } },
      { label: "Permanent shade structure", weights: { pergola: 3, metal: 2, sail: 2 } },
      { label: "Block sun + wind from the side", weights: { screen: 3 } },
      { label: "Cover a big open yard / playground", weights: { sail: 3 } },
    ],
  },
  {
    id: "wall",
    q: "Do you have a house wall to mount to?",
    answers: [
      { label: "Yes — solid wall right above the patio", weights: { solair: 3, screen: 2, metal: 2 } },
      { label: "Yes but limited height / soffit only", weights: { metal: 2, screen: 1, pergola: 1 } },
      { label: "No — open yard or detached patio", weights: { sail: 3, pergola: 2 } },
    ],
  },
  {
    id: "wind",
    q: "How windy does it get? (Hill Country, lake, ridge)",
    answers: [
      { label: "Sheltered / suburban", weights: { solair: 3, screen: 2, pergola: 1 } },
      { label: "Average San Antonio / Austin", weights: { solair: 2, metal: 2, sail: 1 } },
      { label: "Very windy / Hill Country / lakefront", weights: { metal: 3, pergola: 2, sail: 1 } },
    ],
  },
  {
    id: "motor",
    q: "Want motorization / smart-home control?",
    answers: [
      { label: "Yes — remote, wind sensor, Alexa", weights: { solair: 3, screen: 3 } },
      { label: "Manual is fine", weights: { metal: 2, pergola: 2, sail: 2 } },
    ],
  },
  {
    id: "budget",
    q: "Rough budget?",
    answers: [
      { label: "Entry ($1.5K–$3K)", weights: { sail: 3, pergola: 1 } },
      { label: "Mid ($3K–$7K)", weights: { solair: 2, screen: 2, metal: 2, pergola: 2 } },
      { label: "Premium ($7K+)", weights: { solair: 3, screen: 3, metal: 2, pergola: 3 } },
    ],
  },
];

const PRODUCT_INFO: Record<Product, { title: string; blurb: string; to: string }> = {
  solair: { title: "Solair® Retractable Awning", blurb: "Motorized lateral-arm awning that extends from your wall on demand. Sunbrella® fabric, Somfy® motor, wind sensor option. Best for San Antonio & Austin patios with a solid wall.", to: "/retractable-awnings" },
  screen: { title: "Motorized Drop Screen (Side-Track)", blurb: "Vertical motorized screen that drops from above to block sun, glare and bugs. Great for wind block on Hill Country patios.", to: "/curtains" },
  sail: { title: "Custom Sail Shade", blurb: "Tensioned Sunbrella sail shade — perfect for open yards, playgrounds, parking and pools. Welded steel posts, anchored for Texas wind.", to: "/sail-shades" },
  pergola: { title: "Pergola / Patio Cover", blurb: "Fixed shade structure — fabric or metal top over welded frame. Permanent shade, no moving parts.", to: "/pergola-covers" },
  metal: { title: "Custom Metal Awning", blurb: "Welded aluminum or steel awning — fixed, permanent and storm-rated. Built for daily Texas sun and wind.", to: "/metal-awnings" },
};

function QuizPage() {
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const allDone = QUESTIONS.every((q) => q.id in answers);

  const scores: Record<Product, number> = { solair: 0, sail: 0, screen: 0, pergola: 0, metal: 0 };
  for (const q of QUESTIONS) {
    const idx = answers[q.id];
    if (idx === undefined) continue;
    const w = q.answers[idx]?.weights || {};
    for (const [k, v] of Object.entries(w)) scores[k as Product] += v ?? 0;
  }
  const winner = (Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0] as Product) || "solair";

  const reset = () => setAnswers({});

  const select = (qid: string, idx: number) => {
    setAnswers((a) => ({ ...a, [qid]: idx }));
    if (typeof window !== "undefined") {
      const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ event: "shade_quiz_answer", question: qid, answer: idx });
    }
  };

  return (
    <div className="min-h-screen bg-cream text-navy-deep">
      <SiteHeader />
      <section className="bg-navy-deep py-14 text-white">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <Breadcrumbs items={[{ label: "Find Your Shade" }]} className="text-white/80" />
          <h1 className="mt-4 font-display text-4xl font-bold uppercase leading-tight md:text-5xl">Find Your Shade</h1>
          <p className="mt-4 max-w-2xl text-white/85">5 questions, ~60 seconds. We'll recommend the right shade product for your San Antonio or Austin patio — retractable awning, motorized drop screen, sail shade, pergola or welded metal.</p>
        </div>
      </section>

      <section className="bg-cream py-12">
        <div className="mx-auto max-w-3xl px-4 lg:px-8 space-y-8">
          {QUESTIONS.map((q, qi) => (
            <div key={q.id} className="border-2 border-navy-deep/15 bg-white p-6">
              <div className="font-display text-xs uppercase tracking-[0.25em] text-red">Question {qi + 1} / {QUESTIONS.length}</div>
              <h2 className="mt-1 font-display text-lg font-bold uppercase md:text-xl">{q.q}</h2>
              <div className="mt-4 grid gap-2">
                {q.answers.map((a, ai) => {
                  const selected = answers[q.id] === ai;
                  return (
                    <button key={ai} type="button" onClick={() => select(q.id, ai)}
                      className={`text-left rounded-sm border-2 px-4 py-3 text-sm transition ${selected ? "border-red bg-red/10 font-semibold" : "border-navy-deep/15 hover:border-red/60"}`}>
                      {a.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {allDone && (
            <div className="border-2 border-red bg-white p-6">
              <div className="font-display text-xs uppercase tracking-[0.25em] text-red">★ YOUR RECOMMENDATION ★</div>
              <h2 className="mt-2 font-display text-2xl font-bold uppercase md:text-3xl">{PRODUCT_INFO[winner].title}</h2>
              <p className="mt-3 text-navy-deep/85">{PRODUCT_INFO[winner].blurb}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to={PRODUCT_INFO[winner].to} className="inline-flex items-center gap-2 bg-red px-5 py-3 font-display text-xs font-extrabold uppercase tracking-wider text-white hover:bg-red-deep">See {PRODUCT_INFO[winner].title} <ArrowRight className="h-3 w-3" /></Link>
                <Link to="/estimate-quotes" className="inline-flex items-center gap-2 border-2 border-navy-deep px-5 py-3 font-display text-xs font-extrabold uppercase tracking-wider text-navy-deep hover:bg-navy-deep hover:text-white">Get A Written Quote</Link>
                <button type="button" onClick={reset} className="inline-flex items-center gap-2 px-3 py-3 font-display text-xs font-extrabold uppercase tracking-wider text-navy-deep/70 hover:text-red"><RefreshCw className="h-3 w-3" /> Start Over</button>
              </div>
            </div>
          )}
        </div>
      </section>

      <QuoteCTA title="Not sure? We'll help you pick." subtitle="Send photos of your patio — we'll recommend the right product and send a written quote." />
      <SiteFooter />
    </div>
  );
}
